import { datalabHttp } from '@dbl/services/common';

import { transformGetNewDataTableResponseDtoToVo } from './transformers';
import type {
  IGetDataTablePreviewDataRequest,
  IGetNewCategoryRequest,
  IGetNewDataTableRequest,
  IGetNewDataTableResponseDto,
  IGetNewDataTableResponseVo,
  IGetTableFilterOptionsRequest,
  IGetTableSchemeRequest,
  IRefreshTableInfoRequest,
} from './types';

/**
 * 数据表管理 API（类目、列表、筛选、预览、同步等）。
 *
 * 原始实现：`src/pages/datalab/pages/Number/DataManagement/service.ts`
 */

/** 获取数据表类目（一级/二级类目、周期、数据库等筛选项）。 */
export function getNewCategory(
  params: IGetNewCategoryRequest,
): Promise<unknown> {
  return datalabHttp({
    url: '/api/v1/datalab/new_getTableLabel',
    method: 'post',
    data: params,
  });
}

/**
 * 查询数据表列表（`new_tablelist` · wire DTO）。
 *
 * 迁移期 L1 仍调用本函数消费 snake_case DTO；新代码请用 {@link getNewDataTableVo}。
 *
 * 无上传 prepare：请求体为 snake_case {@link IGetNewDataTableRequest}（标量/简单数组），
 * L1 直接传 wire；无双态 JSON stringify、无上传 VO、无 kind 分派。
 *
 * 原始实现：`src/pages/datalab/pages/Number/DataManagement/service.ts`
 */
export function getNewDataTable(
  params: IGetNewDataTableRequest,
): Promise<IGetNewDataTableResponseDto> {
  return datalabHttp({
    url: '/api/v1/datalab/new_tablelist',
    method: 'post',
    data: params,
  });
}

/**
 * 查询数据表列表（前端 VO · camelCase）。
 *
 * HTTP 返回后在边界层执行 DTO→VO（{@link transformGetNewDataTableResponseDtoToVo}）。
 *
 * 标杆：`services/chart/chartDetail/api.ts` · `getChartListVo`。
 */
export async function getNewDataTableVo(
  params: IGetNewDataTableRequest,
): Promise<IGetNewDataTableResponseVo> {
  const dto = await getNewDataTable(params);
  return transformGetNewDataTableResponseDtoToVo(dto);
}

/** 获取表过滤选项（驱动、库名等）。 */
export function getTableFilterOptions(
  params: IGetTableFilterOptionsRequest,
): Promise<unknown> {
  return datalabHttp({
    url: '/api/v1/datalab/table_filter_options',
    method: 'post',
    data: params,
  });
}

/** 根据表名获取跳转经分的地址。 */
export function getTableScheme(
  params: IGetTableSchemeRequest,
): Promise<unknown> {
  return datalabHttp({
    url: '/api/v1/datalab/getTableScheme',
    method: 'post',
    data: params,
  });
}

/**
 * 获取单表预览数据（最多 20 条）。
 * 与 `common/getTablePreviewData`（多表关联 `table_data_preview`）区分。
 */
export function getDataTablePreviewData(
  params: IGetDataTablePreviewDataRequest,
): Promise<unknown> {
  return datalabHttp({
    url: '/api/v1/datalab/getTablePreviewData',
    method: 'post',
    data: params,
  });
}

/** 刷新数据表信息。 */
export function refreshTableInfo(
  params: IRefreshTableInfoRequest,
): Promise<unknown> {
  return datalabHttp({
    url: '/api/v1/datalab/syncDb',
    method: 'post',
    data: params,
  });
}
